import { RefabricProURL } from "../index";
import serializeParams from "../serializeParams";
import {
  IBalance,
  ICreateEndpointRequest,
  ICreateKeyRequest,
  IDeliveriesPage,
  IDeliveriesRequest,
  IDeliveryDetail,
  IDeliveryQueued,
  IDeliveryStatusList,
  IEndpoint,
  IEndpointsResponse,
  IErrorList,
  IFileDeleted,
  IHandledResponse,
  IIssuedKey,
  IJob,
  IJobsPage,
  IJobsRequest,
  IKey,
  IKeysResponse,
  ILimits,
  IMeta,
  IMetrics,
  IMetricsRequest,
  IRefsPage,
  IRefsRequest,
  IRequestsPage,
  IRequestsRequest,
  IRollKeyRequest,
  IScopesResponse,
  ITaskList,
  IUpdateEndpointRequest,
  IUpdateKeyRequest,
  IUsage,
  IUsageRequest,
  IVocabEntry,
  IWebhookEventsResponse,
} from "./developer.type";

// The Developers panel's reads and writes — the session-only `/v1/developer/*` routes and the public
// routes called with the signed-in session (`/v1/jobs`, `/v1/account/*`, `/v1/meta`, `/v1/vocab`, …).
// Each resolves to the JSON body itself (HttpClient returns `response.data`), or — on a 401 only — to
// the interceptor's `_handled` stub (`[HTTP_HANDLED_RESPONSE]`); callers check that first. Repeatable filters go through `serializeParams` (bare repeated keys).
type Body<T> = Promise<T | IHandledResponse>;

// K99: every panel call carries `v3Errors`, so its 403 / 409 / 503 reach the screen as the API's own
// error (no logout, no snackbar); a 401 still signs out.
const V3 = { v3Errors: true } as const;
const params = (p: object) => ({ ...V3, params: p, paramsSerializer: serializeParams });

export default {
  /* ── API keys ── */
  listScopes(): Body<IScopesResponse> {
    return RefabricProURL.get("developer/api-keys/scopes", V3) as unknown as Body<IScopesResponse>;
  },
  listKeys(): Body<IKeysResponse> {
    return RefabricProURL.get("developer/api-keys", V3) as unknown as Body<IKeysResponse>;
  },
  getKey(keyId: string): Body<IKey> {
    return RefabricProURL.get(`developer/api-keys/${encodeURIComponent(keyId)}`, V3) as unknown as Body<IKey>;
  },
  createKey(data: ICreateKeyRequest): Body<IIssuedKey> {
    return RefabricProURL.post("developer/api-keys", data, V3) as unknown as Body<IIssuedKey>;
  },
  updateKey(keyId: string, data: IUpdateKeyRequest): Body<IKey> {
    return RefabricProURL.patch(`developer/api-keys/${encodeURIComponent(keyId)}`, data, V3) as unknown as Body<IKey>;
  },
  rollKey(keyId: string, data: IRollKeyRequest): Body<IIssuedKey> {
    return RefabricProURL.post(`developer/api-keys/${encodeURIComponent(keyId)}/roll`, data, V3) as unknown as Body<IIssuedKey>;
  },
  revokeKey(keyId: string): Body<IKey> {
    return RefabricProURL.delete(`developer/api-keys/${encodeURIComponent(keyId)}`, V3) as unknown as Body<IKey>;
  },

  /* ── webhook endpoints + delivery log ── */
  listWebhookEvents(): Body<IWebhookEventsResponse> {
    return RefabricProURL.get("developer/webhook-endpoints/events", V3) as unknown as Body<IWebhookEventsResponse>;
  },
  listEndpoints(): Body<IEndpointsResponse> {
    return RefabricProURL.get("developer/webhook-endpoints", V3) as unknown as Body<IEndpointsResponse>;
  },
  getEndpoint(endpointId: string): Body<IEndpoint> {
    return RefabricProURL.get(`developer/webhook-endpoints/${encodeURIComponent(endpointId)}`, V3) as unknown as Body<IEndpoint>;
  },
  createEndpoint(data: ICreateEndpointRequest): Body<IEndpoint> {
    return RefabricProURL.post("developer/webhook-endpoints", data, V3) as unknown as Body<IEndpoint>;
  },
  updateEndpoint(endpointId: string, data: IUpdateEndpointRequest): Body<IEndpoint> {
    return RefabricProURL.patch(`developer/webhook-endpoints/${encodeURIComponent(endpointId)}`, data, V3) as unknown as Body<IEndpoint>;
  },
  deleteEndpoint(endpointId: string): Body<null> {
    return RefabricProURL.delete(`developer/webhook-endpoints/${encodeURIComponent(endpointId)}`, V3) as unknown as Body<null>;
  },
  pingEndpoint(endpointId: string): Body<IDeliveryQueued> {
    return RefabricProURL.post(`developer/webhook-endpoints/${encodeURIComponent(endpointId)}/ping`, {}, V3) as unknown as Body<IDeliveryQueued>;
  },
  listDeliveryStatuses(): Body<IDeliveryStatusList> {
    return RefabricProURL.get("developer/webhook-deliveries/statuses", V3) as unknown as Body<IDeliveryStatusList>;
  },
  listDeliveries(p: IDeliveriesRequest): Body<IDeliveriesPage> {
    return RefabricProURL.get("developer/webhook-deliveries", params(p)) as unknown as Body<IDeliveriesPage>;
  },
  getDelivery(deliveryId: string, expand?: string[]): Body<IDeliveryDetail> {
    return RefabricProURL.get(`developer/webhook-deliveries/${encodeURIComponent(deliveryId)}`, params({ expand })) as unknown as Body<IDeliveryDetail>;
  },
  replayDelivery(deliveryId: string): Body<IDeliveryQueued> {
    return RefabricProURL.post(`developer/webhook-deliveries/${encodeURIComponent(deliveryId)}/replay`, {}, V3) as unknown as Body<IDeliveryQueued>;
  },

  /* ── jobs ── */
  listJobs(p: IJobsRequest): Body<IJobsPage> {
    return RefabricProURL.get("jobs", params(p)) as unknown as Body<IJobsPage>;
  },
  getJob(jobId: string, expand?: string[]): Body<IJob> {
    return RefabricProURL.get(`jobs/${encodeURIComponent(jobId)}`, params({ expand })) as unknown as Body<IJob>;
  },

  /* ── account ── */
  getBalance(): Body<IBalance> {
    return RefabricProURL.get("account/balance", V3) as unknown as Body<IBalance>;
  },
  getUsage(p: IUsageRequest): Body<IUsage> {
    return RefabricProURL.get("account/usage", params(p)) as unknown as Body<IUsage>;
  },
  getMetrics(p: IMetricsRequest): Body<IMetrics> {
    return RefabricProURL.get("account/metrics", params(p)) as unknown as Body<IMetrics>;
  },
  listRequests(p: IRequestsRequest): Body<IRequestsPage> {
    return RefabricProURL.get("account/requests", params(p)) as unknown as Body<IRequestsPage>;
  },
  getLimits(): Body<ILimits> {
    return RefabricProURL.get("account/limits", V3) as unknown as Body<ILimits>;
  },

  /* ── reference reads ── */
  getMeta(): Body<IMeta> {
    return RefabricProURL.get("meta", V3) as unknown as Body<IMeta>;
  },
  getVocab(name: string): Body<IVocabEntry> {
    return RefabricProURL.get(`vocab/${encodeURIComponent(name)}`, V3) as unknown as Body<IVocabEntry>;
  },
  listErrors(): Body<IErrorList> {
    return RefabricProURL.get("errors", V3) as unknown as Body<IErrorList>;
  },
  listTasks(): Body<ITaskList> {
    return RefabricProURL.get("tasks", V3) as unknown as Body<ITaskList>;
  },

  /* ── files & records ── */
  listRefs(kind: string, p: IRefsRequest): Body<IRefsPage> {
    return RefabricProURL.get(`refs/${encodeURIComponent(kind)}`, params(p)) as unknown as Body<IRefsPage>;
  },
  deleteFile(ref: string): Body<IFileDeleted> {
    return RefabricProURL.delete(`files/${encodeURIComponent(ref)}`, V3) as unknown as Body<IFileDeleted>;
  },
};
